import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Activity, Filter, RefreshCw, ExternalLink } from 'lucide-react';
import ledgerApi from '../lib/ledgerApi';

export default function ActivityFeed({ user, businesses }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterAction, setFilterAction] = useState('all');

  // Businesses the user has access to AND that have a ledger entity ID
  const linkedBusinesses = useMemo(
    () => businesses.filter((b) => b.roles?.[user.uid] && b.ledgerEntityId),
    [businesses, user.uid]
  );

  const load = async () => {
    setLoading(true);
    setErrors([]);
    const errs = [];
    const all = [];
    await Promise.all(
      linkedBusinesses.map(async (b) => {
        try {
          const rows = await ledgerApi.audit(b.ledgerEntityId, { limit: 30 });
          rows.forEach((r) => all.push({ ...r, businessId: b.id, businessName: b.name, entityId: b.ledgerEntityId }));
        } catch (err) {
          errs.push({ business: b.name, error: err.message });
        }
      })
    );
    all.sort((a, b) => new Date(b.ts) - new Date(a.ts));
    setEntries(all);
    setErrors(errs);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [linkedBusinesses.length]);

  const visibleEntries = useMemo(() => {
    return entries.filter((e) => {
      if (filterEntity !== 'all' && e.entityId !== filterEntity) return false;
      if (filterAction !== 'all' && e.action !== filterAction) return false;
      return true;
    });
  }, [entries, filterEntity, filterAction]);

  const distinctActions = useMemo(() => {
    return Array.from(new Set(entries.map((e) => e.action))).sort();
  }, [entries]);

  const fmtTs = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const actionColor = (action) => {
    if (action?.includes('create') || action?.includes('insert')) return '#5b8b6c';
    if (action?.includes('update') || action?.includes('edit')) return '#7a8b8f';
    if (action?.includes('delete') || action?.includes('remove')) return '#b54a4a';
    if (action?.includes('match') || action?.includes('reconcile')) return '#5b6b8b';
    return '#6b6b6b';
  };

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="header-left">
          <Link to="/" className="back-btn" title="Back"><ArrowLeft size={18} /></Link>
          <h1 className="app-title"><Activity size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />Activity</h1>
        </div>
        <div className="header-right">
          <button className="btn btn-ghost" onClick={load} title="Refresh">
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </header>

      <main className="activity-main">
        {/* Filters */}
        <div className="activity-filters">
          <div className="activity-filter-group">
            <label><Filter size={12} /> Business</label>
            <select value={filterEntity} onChange={(e) => setFilterEntity(e.target.value)}>
              <option value="all">All ({linkedBusinesses.length})</option>
              {linkedBusinesses.map((b) => (
                <option key={b.ledgerEntityId} value={b.ledgerEntityId}>{b.name}</option>
              ))}
            </select>
          </div>
          <div className="activity-filter-group">
            <label><Filter size={12} /> Action</label>
            <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)}>
              <option value="all">All actions</option>
              {distinctActions.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div className="activity-count">
            {visibleEntries.length} of {entries.length} entries
          </div>
        </div>

        {errors.length > 0 && (
          <div className="activity-errors">
            {errors.map((e, i) => (
              <div key={i} className="activity-error">
                Failed to load <strong>{e.business}</strong>: {e.error}
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="activity-empty">Loading activity...</div>
        ) : linkedBusinesses.length === 0 ? (
          <div className="activity-empty">
            No businesses linked to a Ledger entity yet. Set <code>ledgerEntityId</code> in business settings to start seeing activity.
          </div>
        ) : visibleEntries.length === 0 ? (
          <div className="activity-empty">No activity matches these filters.</div>
        ) : (
          <div className="activity-list">
            {visibleEntries.map((e) => (
              <div key={`${e.entityId}-${e.id}`} className="activity-row">
                <div className="activity-row-meta">
                  <span className="activity-business">{e.businessName}</span>
                  <span className="activity-time">{fmtTs(e.ts)}</span>
                </div>
                <div className="activity-row-main">
                  <span className="activity-action" style={{ color: actionColor(e.action) }}>
                    {e.action}
                  </span>
                  <span className="activity-record">
                    {e.record_type}{e.record_id ? ` #${e.record_id}` : ''}
                  </span>
                  {e.user_email && <span className="activity-user">by {e.user_email}</span>}
                </div>
                {e.payload && Object.keys(e.payload).length > 0 && (
                  <div className="activity-payload">
                    {JSON.stringify(e.payload).slice(0, 180)}
                    {JSON.stringify(e.payload).length > 180 ? '…' : ''}
                  </div>
                )}
                <a
                  href={ledgerApi.deepLink(e.entityId, e.record_type === 'transaction' ? 'transactions' : 'audit')}
                  target="_blank" rel="noreferrer"
                  className="activity-jump"
                  title="Open in Ledger"
                >
                  <ExternalLink size={12} />
                </a>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
